import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { Trans, useTranslation } from 'react-i18next';
import { ParcelReference } from '~/lib/types/tracking-types';
import { P2GButton, P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { PublicSettings } from '~/lib/ui-serverless-settings';
import { TranslationNamespaces } from '~/lib/constants';

interface IExternalTrackingLinkProps {
  link: string;
  trackingNumber: string | null | undefined;
  parcelReferences: ParcelReference[];
  postcode: string | null | undefined;
  keyEvents: { [type: string]: string };
  showMessage: boolean;
  sectionStyle?: 'infoCard' | 'order' | 'trackingTable';
}

const useStyles = makeStyles()(() => ({
  localCourierText: { marginTop: '20px' }
}));

const ExternalTrackingLink: React.FC<IExternalTrackingLinkProps> = (props) => {
  const { classes } = useStyles();
  const { t } = useTranslation(TranslationNamespaces.TRACKING);
  const { settings } = useTenant();

  const shouldDisplay = React.useMemo(() => {
    return (
      props.keyEvents['Entered Customs'] ||
      props.keyEvents['Left Customs'] ||
      props.keyEvents['Left Origin Country'] ||
      props.keyEvents['Held Customs']
    );
  }, [props.keyEvents]);

  const collectionDate = props.keyEvents['Collected']?.split('T')[0];

  const link = React.useMemo(() => {
    if (!props.link) {
      return;
    }

    return Object.entries(props.parcelReferences).reduce(
      (rawUrl, [, value]) => {
        return rawUrl.replace(`{${value.type}}`, value.reference);
      },
      props.link.replace('{Postcode}', props.postcode || '').replace('{CollectionDate}', collectionDate || '')
    );
  }, [collectionDate, props.link, props.parcelReferences, props.postcode]);

  if (!shouldDisplay) {
    return <P2GGrid item></P2GGrid>;
  }

  if (props.sectionStyle === 'trackingTable') {
    return (
      <>
        <P2GGrid item>
          <P2GLink href={link} target="_blank" relationship="noopener noreferrer" underline="none">
            <P2GButton variant="tertiary">
              <Trans t={t} i18nKey="RESULT_INFORMATION_CARDS_LOCAL_TRACKING_BUTTON" defaults="View Local Tracking" /> *
            </P2GButton>
          </P2GLink>
        </P2GGrid>
        <P2GGrid item={true} flexGrow={1}>
          <P2GText small={true}>
            <Trans
              t={t}
              i18nKey="HISTORY_PARCEL_LOCAL_TRACKING_DISCLAIMER"
              defaults="* To view local tracking you will be taken to a third party website which has no association with {{company}}"
              values={{ company: settings[PublicSettings.COMPANYNAME] }}
            />
          </P2GText>
        </P2GGrid>
      </>
    );
  }

  if (props.sectionStyle === 'infoCard') {
    return (
      <P2GLink href={link} target="_blank" relationship="noopener noreferrer" underline="none">
        <P2GButton variant="secondary">
          <Trans t={t} i18nKey="RESULT_INFORMATION_CARDS_LOCAL_TRACKING_BUTTON" defaults="View Local Tracking" />
        </P2GButton>
      </P2GLink>
    );
  }

  return (
    <>
      <P2GGrid item xs={6}>
        <P2GLink href={link} target="_blank" relationship="noopener noreferrer" underline="none">
          <P2GButton variant="tertiary">
            <Trans t={t} i18nKey="RESULT_EXTERNAL_BUTTON">
              Check Local Tracking
            </Trans>
          </P2GButton>
        </P2GLink>
      </P2GGrid>
      {props.showMessage && (
        <P2GGrid item xs={12} classNames={classes.localCourierText}>
          <P2GText>
            <Trans t={t} i18nKey="RESULT_EXTERNAL_TEXT">
              We recommend checking the local delivery partner tracking for the latest delivery updates and customs
              information.
            </Trans>
          </P2GText>
        </P2GGrid>
      )}
    </>
  );
};

export default ExternalTrackingLink;
